import { Box, ChevronRightIcon } from '@deckee/deck-hand';
import React, { FC, ReactNode, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import CollapseSlideTransition from '../../../../shared/CollapseSlideTransition/CollapseSlideTransition';
import {
  SLIDE_DURATION_MS,
  SLIDE_EASING,
} from '../../../../shared/slideTransition/slideTransition';
import type { WeatherForecastCardName } from '../../helpers/analytics/weatherForecastAnalytics';
import { useWeatherForecastCardExpanded } from '../../hooks/useWeatherForecastCardExpanded';

const SectionHeader = styled(Box)`
  cursor: pointer;
  user-select: none;
`;

const ChevronWrapper = styled(Box)<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  transition: transform ${SLIDE_DURATION_MS}ms ${SLIDE_EASING};
  transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
`;

export interface CollapsibleSectionProps {
  title: string;
  testId?: string;
  analyticsCard: WeatherForecastCardName;
  defaultExpanded?: boolean;
  children: ReactNode;
}

const CollapsibleSection: FC<CollapsibleSectionProps> = ({
  title,
  testId,
  analyticsCard,
  defaultExpanded = true,
  children,
}) => {
  const theme = useTheme();
  const { isExpanded, toggleExpanded } = useWeatherForecastCardExpanded(
    analyticsCard,
    defaultExpanded,
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleExpanded();
      }
    },
    [toggleExpanded],
  );

  return (
    <Box mt={3} data-testid={testId}>
      <SectionHeader
        display="flex"
        alignItems="center"
        fontSize={1}
        fontWeight={600}
        color="darkergrey"
        mb={2}
        minHeight="20px"
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        data-testid={testId ? `${testId}-header` : undefined}
      >
        <Box flex="1 1 auto" lineHeight="1.2">
          {title}
        </Box>
        <ChevronWrapper $expanded={isExpanded} ml={2}>
          <ChevronRightIcon color={theme.colors.darkgrey} size={16} />
        </ChevronWrapper>
      </SectionHeader>

      <CollapseSlideTransition expanded={isExpanded}>
        {children}
      </CollapseSlideTransition>
    </Box>
  );
};

export default CollapsibleSection;
